import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { showToast } from '../../../contexts/ToastNotification';

const useAdminUsers = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({ search: '', role: '' });
    const [updatingUser, setUpdatingUser] = useState(null);
    const [stats, setStats] = useState({
        total: 0,
        active: 0,
        admin: 0,
        verified: 0,
        pending: 0
    });

    // Carica tutti gli utenti
    const fetchUsers = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get('/api/admin/users', {
                params: { limit: 100 }
            });
            
            if (response.data.success) {
                const usersList = response.data.users || [];
                setUsers(usersList);
                
                // Aggiorna statistiche
                setStats({
                    total: usersList.length,
                    active: usersList.filter(u => u.is_attivo).length,
                    admin: usersList.filter(u => u.status_utente === 'admin').length,
                    verified: usersList.filter(u => u.email_verificata).length,
                    pending: usersList.filter(u => !u.email_verificata).length
                });
            } else {
                throw new Error(response.data.message || 'Errore nel caricamento');
            }
        } catch (err) {
            const errorMessage = err.response?.data?.message || err.message || 'Errore nel caricamento degli utenti';
            setError(errorMessage);
            showToast(errorMessage, 'error');
            console.error('❌ Errore fetch utenti:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    // Aggiorna ruolo o stato utente
    const updateUserStatus = useCallback(async (userId, statusData) => {
        setUpdatingUser(userId);
        try {
            const response = await axios.put(`/api/admin/users/${userId}/status`, statusData);

            if (response.data.success) {
                // Aggiorna l'utente nella lista
                setUsers(prev => prev.map(user => 
                    user.id === userId ? { ...user, ...response.data.user } : user
                ));
                
                // Aggiorna statistiche
                if (statusData.status_utente !== undefined) {
                    const isAdmin = statusData.status_utente === 'admin';
                    const wasAdmin = users.find(u => u.id === userId)?.status_utente === 'admin';
                    setStats(prev => ({
                        ...prev,
                        admin: isAdmin ? prev.admin + 1 : wasAdmin ? prev.admin - 1 : prev.admin
                    }));
                }
                
                if (statusData.is_attivo !== undefined) {
                    setStats(prev => ({
                        ...prev,
                        active: statusData.is_attivo ? prev.active + 1 : prev.active - 1
                    }));
                }
                
                showToast('Aggiornamento completato con successo', 'success');
                return true;
            }
            throw new Error(response.data.message);
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Errore nell\'aggiornamento';
            showToast(errorMessage, 'error');
            console.error('❌ Errore aggiornamento:', error);
            return false;
        } finally {
            setUpdatingUser(null);
        }
    }, [users]);

    // Aggiorna ruolo utente
    const updateUserRole = useCallback(async (userId, newRole) => {
        return updateUserStatus(userId, { status_utente: newRole });
    }, [updateUserStatus]);

    // Attiva/disattiva utente
    const toggleUserStatus = useCallback(async (userId, currentStatus) => {
        return updateUserStatus(userId, { is_attivo: !currentStatus });
    }, [updateUserStatus]);

    // Disabilita utente (soft delete)
    const deleteUser = useCallback(async (userId, userName) => {
        if (!window.confirm(`Sei sicuro di voler disabilitare l'utente ${userName}?`)) {
            return false;
        }
        
        setUpdatingUser(userId);
        try {
            const response = await axios.delete(`/api/admin/users/${userId}`);

            if (response.data.success) {
                setUsers(prev => prev.map(user => 
                    user.id === userId ? { ...user, is_attivo: false } : user
                ));
                showToast(`Utente ${userName} disabilitato con successo`, 'success');
                
                // Aggiorna statistiche attivi
                setStats(prev => ({
                    ...prev,
                    active: prev.active - 1
                }));
                
                return true;
            }
            throw new Error(response.data.message);
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Errore nella disabilitazione dell\'utente';
            showToast(errorMessage, 'error');
            console.error('❌ Errore disabilitazione utente:', error);
            return false;
        } finally {
            setUpdatingUser(null);
        }
    }, []);

    // Reset filtri
    const resetFilters = useCallback(() => {
        setFilters({ search: '', role: '' });
    }, []);

    // Filtra utenti localmente
    const getFilteredUsers = useCallback(() => {
        return users.filter(user => {
            if (filters.search) {
                const searchLower = filters.search.toLowerCase();
                const emailMatch = user.email_utente?.toLowerCase().includes(searchLower);
                const nomeMatch = user.nome_utente?.toLowerCase().includes(searchLower);
                const cognomeMatch = user.cognome_utente?.toLowerCase().includes(searchLower);
                if (!emailMatch && !nomeMatch && !cognomeMatch) {
                    return false;
                }
            }
            if (filters.role && user.status_utente !== filters.role) {
                return false;
            }
            return true;
        });
    }, [users, filters]);

    // Carica utenti all'inizializzazione
    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    return {
        // Stato
        users,
        loading,
        error,
        filters,
        updatingUser,
        stats,
        filteredUsers: getFilteredUsers(),
        
        // Azioni
        setFilters,
        fetchUsers,
        updateUserRole,
        toggleUserStatus,
        deleteUser,
        resetFilters,
        
        // Helper
        isUpdating: (userId) => updatingUser === userId
    };
};

export default useAdminUsers;
